import { Scissors, Trees, Leaf, Sprout, Trash2, Phone, Mail, Share2, Facebook, Linkedin, Link2, CheckCircle2, ArrowRight, Sparkles, MapPin } from "lucide-react";
import { useState } from "react";

const HALLOWEEN_IMG =
  "https://images.pexels.com/photos/19127451/pexels-photo-19127451.jpeg?auto=compress&cs=tinysrgb&w=1600";

const SHARE_URL = "https://ironclad-atx.com/#halloween";
const SHARE_TEXT =
  "👻 Don't let your yard become the scariest thing on the block! Ironclad Yard Services — now serving San Antonio & surrounding areas. Halloween Special on now.";

const SERVICES = [
  { icon: Scissors, label: "Lawn Mowing" },
  { icon: Leaf, label: "Hedge Trimming" },
  { icon: Trash2, label: "Leaf Cleanup" },
  { icon: Sprout, label: "Mulch Installation" },
  { icon: Trees, label: "Property Cleanups" },
];

export default function HalloweenSpecial() {
  const [copied, setCopied] = useState(false);

  const openShare = (url) => window.open(url, "_blank", "noopener,noreferrer,width=640,height=600");

  const handleFacebookShare = () =>
    openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}&quote=${encodeURIComponent(SHARE_TEXT)}`);
  const handleLinkedInShare = () =>
    openShare(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SHARE_URL)}`);
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard blocked — ignore
    }
  };

  return (
    <section
      id="halloween"
      data-testid="halloween-section"
      className="relative bg-black border-y border-border overflow-hidden"
    >
      {/* Backdrop image + overlays */}
      <div className="absolute inset-0">
        <img
          src={HALLOWEEN_IMG}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60" />
        <div
          className="absolute inset-0 mix-blend-screen opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 20% 30%, rgba(255,140,0,0.22), transparent 55%), radial-gradient(ellipse at 80% 70%, rgba(127,176,105,0.18), transparent 55%)",
          }}
        />
      </div>

      <div className="container-ic relative py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT — Copy */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-black mb-6 animate-pulse">
              <Sparkles className="h-3.5 w-3.5" />
              Halloween Special · October
            </div>

            <h2
              className="font-display italic text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9]"
              data-testid="hw-headline"
            >
              Don&apos;t let your yard <br />
              become the <span className="text-primary">scariest</span> <br />
              thing on the block.
            </h2>

            <p className="mt-7 text-base md:text-lg text-foreground/90 leading-relaxed max-w-2xl">
              Overgrown grass? Creepy weeds? Haunted hedges? Before the ghosts
              and goblins arrive, let Ironclad whip your yard into shape.
            </p>

            {/* Service checklist */}
            <ul className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SERVICES.map((s) => {
                const Icon = s.icon;
                return (
                  <li
                    key={s.label}
                    className="flex items-center gap-3 bg-black/60 backdrop-blur-sm border border-primary/30 px-4 py-3 hover:border-primary transition-colors"
                    data-testid={`hw-svc-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="w-8 h-8 border border-primary/50 bg-primary/10 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary/80" />
                      <span className="font-display text-sm md:text-base uppercase tracking-tight text-white font-bold">
                        {s.label}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Location + tagline */}
            <div className="mt-9 flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2 text-primary">
                <MapPin className="h-4 w-4" />
                <span className="uppercase tracking-[0.2em] font-bold">
                  Serving San Antonio &amp; Surrounding Areas
                </span>
              </div>
            </div>
            <div className="mt-5 font-display italic text-white text-xl md:text-2xl font-bold leading-tight">
              We kill weeds, <span className="text-primary">not vibes.</span>
            </div>

            {/* CTAs */}
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href="tel:5129480676"
                className="btn-primary group"
                data-testid="hw-cta-call"
              >
                <Phone className="h-4 w-4" />
                Call 512-948-0676
              </a>
              <a
                href="mailto:ironcladatx@gmail.com?subject=Halloween%20Special%20%E2%80%94%20Yard%20Service%20Request&body=Hi%20Ironclad%2C%20I%27d%20like%20to%20book%20the%20Halloween%20Special%20for%20my%20yard.%0A%0AProperty%20address%3A%20%5B%5D%0AServices%20needed%3A%20%5B%5D%0ATimeline%3A%20before%20trick-or-treating%0AName%3A%20%5B%5D%0APhone%3A%20%5B%5D"
                className="btn-outline"
                data-testid="hw-cta-email"
              >
                <Mail className="h-4 w-4" />
                Email Ironclad
              </a>
            </div>
            <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Give your yard a frightfully good transformation
            </p>

            {/* Share strip */}
            <div className="mt-10" data-testid="hw-share-strip">
              <div className="overline mb-3">
                <Share2 className="inline h-3 w-3 mr-1.5 -mt-0.5" />
                Share the Special
              </div>
              <div className="flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={handleFacebookShare}
                  className="inline-flex items-center gap-2 border border-border bg-black/40 hover:border-primary hover:bg-primary/10 text-white text-xs uppercase tracking-[0.2em] font-bold px-4 py-2.5 transition-colors"
                  data-testid="hw-share-facebook"
                >
                  <Facebook className="h-4 w-4 text-primary" />
                  Facebook
                </button>
                <button
                  type="button"
                  onClick={handleLinkedInShare}
                  className="inline-flex items-center gap-2 border border-border bg-black/40 hover:border-primary hover:bg-primary/10 text-white text-xs uppercase tracking-[0.2em] font-bold px-4 py-2.5 transition-colors"
                  data-testid="hw-share-linkedin"
                >
                  <Linkedin className="h-4 w-4 text-primary" />
                  LinkedIn
                </button>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="inline-flex items-center gap-2 border border-border bg-black/40 hover:border-primary hover:bg-primary/10 text-white text-xs uppercase tracking-[0.2em] font-bold px-4 py-2.5 transition-colors"
                  data-testid="hw-share-copy"
                >
                  {copied ? (
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  ) : (
                    <Link2 className="h-4 w-4 text-primary" />
                  )}
                  {copied ? "Copied!" : "Copy Link"}
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT — Hero image */}
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden border border-primary/30">
              <img
                src={HALLOWEEN_IMG}
                alt="Halloween decorations on a clean, sharply-maintained yard"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="hw-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                <div className="font-display italic text-white text-xl md:text-2xl font-black uppercase tracking-tight leading-tight">
                  Frightfully <br />
                  <span className="text-primary">good</span> curb appeal.
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-primary">
                  <ArrowRight className="h-3 w-3" />
                  Book Before October 31
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
